import { NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CrmTranslatePipe } from 'common-module/translate';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { InputButtonGroupComponent } from '@app/shared/components/autocomplete-search-input/input-button-group.component';
import { MediaService } from '@app/core/media/media.service';
import { PlCardService } from '@app/core/services/pl-card.service';
import { CrmSelectOption } from 'common-module/select';
import { TagComponent } from '@app/shared/components/tag/tag.component';
import { CrmSignatureStatus } from 'common-module/signature';

@Component({
  selector: 'app-pl-card-upload',
  template: `
    <div class="pl-card-upload">
      <h3 class="modal-label">{{ label }}</h3>
      <div class="pl-card-upload__inputs">
        <app-input-group
          class="input-group"
          [btnLabel]="'generic.addBtnLabel' | crmTranslate"
          [placeholder]="placeholder"
          [autocompleteFunction]="onAutoComplete"
          [(ngModel)]="existingFileInput"
          (ngModelChange)="addExistingPlCard()"
        ></app-input-group>
        <span>or</span>
        <app-button
          [title]="'generic.uploadBtnLabel' | crmTranslate"
          [buttonClass]="'ant-btn-secondary'"
          (click)="selectFile()"
        ></app-button>
        <input
          #fileInput
          name="file_input"
          type="file"
          style="display: none;"
          (change)="fileSelected()"
        />
      </div>
      <div *ngIf="selectedFile || existingFile" class="pl-card-upload__files">
        <app-tag
          [title]="existingFile?.name || selectedFile?.name"
          [icon]="'images:pdf'"
          (tagClosed)="resetFiles()"
        ></app-tag>
      </div>
    </div>
  `,
  styleUrls: ['./pl-card-upload.component.less'],
  imports: [
    InputButtonGroupComponent,
    CrmTranslatePipe,
    ButtonComponent,
    NgIf,
    TagComponent,
    FormsModule,
  ],
  standalone: true,
})
export class PlCardUploadComponent {
  public get selectedFile(): File | undefined {
    return this._selectedFile;
  }

  public set selectedFile(val: File | undefined) {
    this._selectedFile = val;
    this.emitChanges(val);
  }

  constructor(
    private mediaService: MediaService,
    private plCardService: PlCardService,
  ) {}

  @ViewChild('fileInput') public fileInput!: ElementRef;
  @Input() public label?: string;
  @Input() public placeholder?: string;
  @Output() public fileChanged = new EventEmitter<File | string>();
  public existingFile?: { name: string; id: string };
  public existingFileInput?: CrmSelectOption;

  private _selectedFile?: File;

  public onAutoComplete = (value: string) => {
    return this.searchMedia(value);
  };

  public addExistingPlCard() {
    if (!this.existingFileInput) {
      return;
    }
    this.existingFile = {
      id: this.existingFileInput.value as any,
      name: this.existingFileInput.label,
    };
    this.existingFileInput = undefined;
    this.emitChanges(this.existingFile.id);
  }

  public selectFile() {
    this.click(this.fileInput);
  }

  private click(element: ElementRef) {
    const event = new MouseEvent('click', { bubbles: false });
    element.nativeElement.dispatchEvent(event);
  }

  public fileSelected() {
    const [file] = this.fileInput.nativeElement.files;
    this.resetFiles();
    this.selectedFile = file;
  }

  public resetFiles() {
    this.selectedFile = undefined;
    this.existingFile = undefined;
  }

  private emitChanges(value: string | File | undefined) {
    this.fileChanged.emit(value);
  }

  private searchMedia(value: string) {
    if (!value) {
      return of([]);
    }
    return this.plCardService
      .listPlCards({
        'status[$nin]': CrmSignatureStatus.voided,
        'meta.media.name[$regex]': value,
        'meta.media.name[$options]': 'i',
      })
      .pipe(
        map(({ data }) => data),
        map((data) =>
          (data ?? []).map((d) => ({
            label: d.name,
            value: d.meta['media']._id,
          })),
        ),
      );
  }
}
